'use client';
import { FC, useState } from 'react';
import { Typography, Box, Alert, Link as MuiLink } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField } from '@/components/common/FormComponents';
import { DataPackageDto, StatusDto } from '@/api/ApiClient';
import { toDoiHref } from '@/lib/utils/doiLinks';

interface SetDoiDialogProps {
  open: boolean;
  dataPackage: DataPackageDto | undefined;
  onClose: (result?: { dataPackageDoi?: string; publicationDoi?: string }) => void;
}

/**
 * Dialog for setting DOI(s) on a published data package
 */
export const SetDoiDialog: FC<SetDoiDialogProps> = ({ open, dataPackage, onClose }) => {
  // Form state - initialized from dataPackage prop
  // Component remounts when key changes (dataPackage.id), so initializer runs with new dataPackage
  const [dataPackageDoi, setDataPackageDoi] = useState(() => dataPackage?.doi || '');
  const [publicationDoi, setPublicationDoi] = useState(() => dataPackage?.publicationDoi || '');
  const [dataPackageDoiError, setDataPackageDoiError] = useState('');
  const [publicationDoiError, setPublicationDoiError] = useState('');

  const handleClose = () => {
    // Reset form when dialog closes
    setDataPackageDoi('');
    setPublicationDoi('');
    setDataPackageDoiError('');
    setPublicationDoiError('');
    onClose();
  };

  const validateDoi = (doi: string): boolean => {
    if (!doi.trim()) {
      return true; // Empty is valid (optional)
    }
    // Basic DOI validation - allow with or without https://doi.org/ prefix
    const doiPattern = /^(https?:\/\/(dx\.)?doi\.org\/)?(10\.\d{4,}\/[^\s]+)$/;
    return doiPattern.test(doi.trim());
  };

  const handleSubmit = () => {
    let hasError = false;

    // Validate data package DOI
    if (dataPackageDoi && !validateDoi(dataPackageDoi)) {
      setDataPackageDoiError('Invalid DOI format. Example: 10.1234/example or https://doi.org/10.1234/example');
      hasError = true;
    } else {
      setDataPackageDoiError('');
    }

    // Validate publication DOI
    if (publicationDoi && !validateDoi(publicationDoi)) {
      setPublicationDoiError('Invalid DOI format. Example: 10.1234/example or https://doi.org/10.1234/example');
      hasError = true;
    } else {
      setPublicationDoiError('');
    }

    if (hasError) {
      return;
    }

    // Check if anything changed
    const dataPackageDoiChanged = dataPackageDoi.trim() !== (dataPackage?.doi || '');
    const publicationDoiChanged = publicationDoi.trim() !== (dataPackage?.publicationDoi || '');

    if (!dataPackageDoiChanged && !publicationDoiChanged) {
      onClose();
      return;
    }

    onClose({
      dataPackageDoi: dataPackageDoi.trim() || undefined,
      publicationDoi: publicationDoi.trim() || undefined,
    });
  };

  const canModifyDataPackageDoi = !dataPackage?.doi; // Can only set if not already set
  const canModifyPublicationDoi = !dataPackage?.publicationDoi; // Can only set if not already set

  const isFormValid =
    dataPackage?.isSnapshot && // Must be snapshot
    (dataPackageDoi.trim() !== '' || publicationDoi.trim() !== '');

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Set DOI"
      onSubmit={handleSubmit}
      submitText="Save"
      submitDisabled={!isFormValid}
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Set the Digital Object Identifier (DOI) for this data package and/or link to an associated publication.
          Publication lookup: Enter a publication DOI that already exists in LiST to link this data package to that
          publication. Once set, DOIs cannot be changed.
        </Typography>

        {!dataPackage?.isSnapshot ? (
          <Alert severity="error">
            DOI can only be assigned to snapshots (immutable data), not live packages. Create a snapshot first.
          </Alert>
        ) : null}

        {dataPackage?.status !== StatusDto.Published ? (
          <Alert severity="warning">
            This data package is not yet published. DOI should typically be set after publishing.
          </Alert>
        ) : null}

        <FormTextField
          label="Data Package DOI"
          value={dataPackageDoi}
          onChange={(e) => setDataPackageDoi(e.target.value)}
          disabled={!canModifyDataPackageDoi}
          error={!!dataPackageDoiError}
          helperText={
            dataPackageDoiError ||
            (canModifyDataPackageDoi
              ? 'Enter the DOI for this data package (e.g., 10.1234/example)'
              : 'DOI is already set and cannot be changed')
          }
          fullWidth
          placeholder="10.1234/example"
        />

        {dataPackageDoi && validateDoi(dataPackageDoi) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <MuiLink
              href={toDoiHref(dataPackageDoi)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <OpenInNew fontSize="small" />
              <Typography variant="caption">Open DOI</Typography>
            </MuiLink>
          </Box>
        ) : null}

        <FormTextField
          label="Publication DOI"
          value={publicationDoi}
          onChange={(e) => setPublicationDoi(e.target.value)}
          disabled={!canModifyPublicationDoi}
          error={!!publicationDoiError}
          helperText={
            publicationDoiError ||
            (canModifyPublicationDoi
              ? 'Link to an existing publication in LiST by entering its DOI. Publication must already exist in LiST.'
              : 'Publication DOI is already set and cannot be changed')
          }
          fullWidth
          placeholder="10.1234/publication"
        />

        {publicationDoi && validateDoi(publicationDoi) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <MuiLink
              href={toDoiHref(publicationDoi)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <OpenInNew fontSize="small" />
              <Typography variant="caption">Open DOI</Typography>
            </MuiLink>
          </Box>
        ) : null}

        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Once a DOI is set:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Data package becomes read-only and cannot be modified or unpublished</li>
              <li>DOI will forward to your share link for access</li>
              <li>Both DOI and share link remain active permanently</li>
            </ul>
          </Typography>
        </Alert>
      </Box>
    </StandardDialog>
  );
};
